import React, { useState, useMemo } from 'react';
import { Input } from '@components/atoms/Input';
import ReactSelect from '@components/atoms/ReactSelect';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { TaskStatusOptions, getTranslatedTaskStatusLabel } from '@utils/options';
import { useActiveTaskCategories } from '@hooks/api/taskCategory';
import { SelectOption } from '@components/atoms/ReactSelect';
import { Task, TaskStatus, TaskDisplayValues } from '@models/task.model';
import { UserRoles } from '@models/user.model';
import { useUpdateTaskStatus } from '@hooks/api';
import { invoiceService } from '@services/invoice';
import { useAppSelector } from '@redux/hooks';
import RichTextEditor from '@components/atoms/RichTextEditor';
import { Badge } from '@components/atoms/Badge';
import { getTaskStatusDisplay, translateTaskDisplayValue } from '@utils/taskDisplay';
import { formatDateOnly } from '@utils/formatDate';
import { Project } from '@models/project.model';
import { useTranslation } from '@hooks/useTranslation';
import {
  ConfirmationDialog,
  AddSubtaskModal,
  SubtasksSection,
  DelegationInfoBanner,
  StatusField,
} from './components';

interface GeneralTabProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void;
  validateField: (field: string) => Promise<void> | Promise<string | undefined>;
  handleBlur: (e: React.FocusEvent<any>) => void;
  errors: any;
  touched: any;
  tasks: Task[];
  task?: Task | null;
  /** Set in subtask mode: the task the new subtask will belong to. */
  parentTask?: Task | null;
  project?: Partial<Project> | null;
  onStatusUpdate?: () => void;
  subtasksToAdd?: string[];
  subtasksToRemove?: string[];
  onSubtasksChange?: (add: string[], remove: string[]) => void;
  hasActiveBudgetRequest?: boolean;
  onCreateSubtaskClick?: () => void;
  onImportTemplateClick?: () => void;
}

export const GeneralTab: React.FC<GeneralTabProps> = ({
  values,
  setFieldValue,
  setFieldTouched,
  validateField,
  handleBlur,
  errors,
  touched,
  tasks,
  task,
  parentTask,
  project,
  onStatusUpdate,
  subtasksToAdd: propSubtasksToAdd = [],
  subtasksToRemove: propSubtasksToRemove = [],
  onSubtasksChange,
  hasActiveBudgetRequest = false,
  onCreateSubtaskClick,
  onImportTemplateClick,
}) => {
  // Sprint 28, story-144. `taskDetail.*` — docs/architecture.md "## Sprint 28" Decision 1.
  const tTaskDetail = useTranslation('taskDetail');
  // State management
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    newStatus: string;
    statusLabel: string;
  } | null>(null);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);
  const [showIncompleteSubtasksDialog, setShowIncompleteSubtasksDialog] =
    useState(false);
  const [pendingCompleteStatusChange, setPendingCompleteStatusChange] =
    useState<{
      newStatus: string;
      statusLabel: string;
      incompleteSubtasksCount: number;
    } | null>(null);
  const [showInvoicePendingDialog, setShowInvoicePendingDialog] = useState(false);
  const [pendingInvoiceCheck, setPendingInvoiceCheck] = useState<{
    newStatus: string;
    statusLabel: string;
    materialCount: number;
    equipmentCount: number;
    sampleNames: string[];
  } | null>(null);

  const { updateTaskStatus, loading: statusLoading } = useUpdateTaskStatus();
  const userRole = useAppSelector((state) => state.user.role);

  // Function to get available status options based on user role and task state
  const getAvailableStatuses = useMemo(() => {
    const isWorkerRole =
      userRole === UserRoles.EMPLOYEE || userRole === UserRoles.CREW_LEAD;
    const isOfficeRole = [
      UserRoles.SUPERINTENDENT,
      UserRoles.PROJECT_MANAGER,
      UserRoles.ADMIN,
      UserRoles.OWNER,
    ].includes(userRole as UserRoles);
    const currentStatus = values.status || task?.status;
    const isNewTask = !task?.taskId;
    const isDelegatedTask = !!task?.originalTaskId;
    const hasDelegatedTask = !!task?.delegateTaskId;
    const isMiddleCompany = isDelegatedTask && hasDelegatedTask;

    // For new tasks
    if (isNewTask) {
      if (isWorkerRole) {
        // Workers can only create tasks as ACTIVE
        return [TaskStatus.ACTIVE];
      } else if (isOfficeRole) {
        // Office roles can create tasks in multiple states
        return [TaskStatus.ACTIVE, TaskStatus.INACTIVE, TaskStatus.CANCELED];
      }
    }

    // For existing tasks based on current status and role
    const availableStatuses: TaskStatus[] = [];

    if (isWorkerRole) {
      // Worker role logic based on current status
      switch (currentStatus) {
        case TaskStatus.ACTIVE:
          // Workers can request completion — unless this copy handed the work to a delegate, in
          // which case its crew isn't doing it and the server refuses the submission.
          availableStatuses.push(TaskStatus.ACTIVE);
          if (!hasDelegatedTask) {
            availableStatuses.push(TaskStatus.INTERNAL_APPROVAL);
          }
          break;
        case TaskStatus.REJECTED:
          // Workers can restart rejected tasks
          availableStatuses.push(TaskStatus.REJECTED, TaskStatus.ACTIVE);
          break;
        default:
          // For other statuses, workers can only view current status
          if (currentStatus) {
            availableStatuses.push(currentStatus as TaskStatus);
          }
          break;
      }
    } else if (isOfficeRole) {
      // Office role logic - hierarchical access with direct control
      switch (currentStatus) {
        case TaskStatus.ACTIVE:
          if (isDelegatedTask) {
            if (isMiddleCompany) {
              // Company B (middle delegator) - can only mark as ACTIVE when working on it
              // Cannot mark CLIENT_APPROVAL until Company C submits it back to them
              availableStatuses.push(TaskStatus.ACTIVE);
            } else {
              // Company C (end delegate) - can mark for client approval to send back up
              availableStatuses.push(
                TaskStatus.ACTIVE,
                TaskStatus.CLIENT_APPROVAL,
              );
            }
          } else {
            // For internal tasks (not delegated to this org), full control
            availableStatuses.push(
              TaskStatus.ACTIVE,
              TaskStatus.INACTIVE,
              TaskStatus.CANCELED,
              TaskStatus.COMPLETE,
            );
          }
          break;
        case TaskStatus.INACTIVE:
          if (isDelegatedTask) {
            // Delegated tasks should not be able to reach INACTIVE state by delegate org
            // This case should theoretically not happen, but if it does, only allow back to ACTIVE
            availableStatuses.push(TaskStatus.INACTIVE, TaskStatus.ACTIVE);
          } else {
            // For internal tasks, full control
            availableStatuses.push(
              TaskStatus.INACTIVE,
              TaskStatus.ACTIVE,
              TaskStatus.CANCELED,
              TaskStatus.COMPLETE,
            );
          }
          break;
        case TaskStatus.INTERNAL_APPROVAL:
          if (isDelegatedTask) {
            // For delegated tasks, can approve to send to client
            availableStatuses.push(
              TaskStatus.INTERNAL_APPROVAL,
              TaskStatus.CLIENT_APPROVAL,
              TaskStatus.REJECTED,
            );
          } else {
            // For internal tasks, can complete or reject
            availableStatuses.push(
              TaskStatus.INTERNAL_APPROVAL,
              TaskStatus.COMPLETE,
              TaskStatus.REJECTED,
            );
          }
          break;
        case TaskStatus.CLIENT_APPROVAL:
          if (isMiddleCompany) {
            // Company B (middle): reviewing its delegate's work as that delegate's client. It can
            // reject here; approving forwards the work on to its own client, which is done from
            // "Needs your action" (it doesn't change B's status, so it has no dropdown option).
            // If B already forwarded, the server refuses and says so.
            availableStatuses.push(TaskStatus.CLIENT_APPROVAL, TaskStatus.REJECTED);
          } else if (hasDelegatedTask) {
            // Company A (top client) - approve or reject, flowing down the whole chain
            availableStatuses.push(
              TaskStatus.CLIENT_APPROVAL,
              TaskStatus.COMPLETE,
              TaskStatus.REJECTED,
            );
          } else {
            // Company C (end delegate) - waiting for client approval, read-only
            availableStatuses.push(TaskStatus.CLIENT_APPROVAL);
          }
          break;
        case TaskStatus.REJECTED:
          if (isMiddleCompany) {
            // Company B (middle): the rework is its delegate's. B's copy follows the delegate's
            // resubmission; pushing it to the client by hand would skip that rework.
            availableStatuses.push(TaskStatus.REJECTED);
          } else if (isDelegatedTask) {
            // For delegated tasks, can only send back for client approval
            availableStatuses.push(
              TaskStatus.REJECTED,
              TaskStatus.CLIENT_APPROVAL,
            );
          } else {
            // For internal tasks, can mark as active to restart work
            availableStatuses.push(TaskStatus.REJECTED, TaskStatus.ACTIVE);
          }
          break;
        case TaskStatus.COMPLETE:
          // Complete status is final, no changes allowed
          availableStatuses.push(TaskStatus.COMPLETE);
          break;
        case TaskStatus.CANCELED:
          if (isDelegatedTask) {
            // Delegated tasks should not be able to reach CANCELED state by delegate org
            // This case should theoretically not happen, but if it does, only allow back to ACTIVE
            availableStatuses.push(TaskStatus.CANCELED, TaskStatus.ACTIVE);
          } else {
            // For internal tasks, allow reactivation
            availableStatuses.push(TaskStatus.CANCELED, TaskStatus.ACTIVE);
          }
          break;
        case TaskStatus.DELEGATION_CANCELED:
          // Delegation canceled is final for that delegation
          availableStatuses.push(TaskStatus.DELEGATION_CANCELED);
          break;
        default:
          availableStatuses.push(TaskStatus.ACTIVE);
          break;
      }
    }

    return availableStatuses;
  }, [
    userRole,
    values.status,
    task?.status,
    task?.taskId,
    task?.originalTaskId,
    task?.delegateTaskId,
  ]);

  // Get existing subtasks (tasks that have current task as parent) plus pending additions
  const existingSubtasks = useMemo(() => {
    if (!task?.taskId) return [];
    const currentSubtasks = tasks.filter((t) => t.parentTaskId === task.taskId);

    // Add tasks that are pending to be added as subtasks
    const tasksToAdd = tasks.filter((t) =>
      propSubtasksToAdd.includes(t.taskId || ''),
    );

    // Remove tasks that are pending to be removed
    const allSubtasks = [...currentSubtasks, ...tasksToAdd];
    const finalSubtasks = allSubtasks.filter(
      (t) => !propSubtasksToRemove.includes(t.taskId || ''),
    );

    return finalSubtasks;
  }, [tasks, task?.taskId, propSubtasksToAdd, propSubtasksToRemove]);

  // Get incomplete subtasks count
  const incompleteSubtasksCount = useMemo(() => {
    return existingSubtasks.filter(
      (subtask) => subtask.status !== TaskStatus.COMPLETE,
    ).length;
  }, [existingSubtasks]);

  // Available tasks for subtasks (exclude current task, tasks with parents, and pending additions)
  const availableTasksForSubtasks: SelectOption[] = useMemo(() => {
    const currentTaskId = task?.taskId;

    return tasks
      .filter((t) => {
        // For create mode (no current task ID), allow all tasks without parents
        // For edit mode, exclude current task
        if (currentTaskId && t.taskId === currentTaskId) return false;
        // Exclude tasks that already have a parent (are already subtasks)
        if (t.parentTaskId) return false;
        // Exclude tasks that are pending to be added
        if (propSubtasksToAdd.includes(t.taskId || '')) return false;
        // Include only tasks with taskId
        return !!t.taskId;
      })
      .map((t) => ({
        value: t.taskId!,
        label: t.name,
      }));
  }, [tasks, task?.taskId, propSubtasksToAdd]);

  // Filter status options based on role and task state
  const statusOptions: SelectOption[] = TaskStatusOptions.filter((option) =>
    getAvailableStatuses.includes(option.id as TaskStatus),
  ).map((option) => ({
    value: option.id,
    label: getTranslatedTaskStatusLabel(option.id, tTaskDetail),
  }));

  // Determine if status dropdown should be disabled and get explanation message
  const statusLockInfo = useMemo(() => {
    const currentStatus = values.status || task?.status;
    const hasDelegatedTask = !!task?.delegateTaskId;
    const isDelegatedTask = !!task?.originalTaskId;
    const isMiddleCompany = isDelegatedTask && hasDelegatedTask;

    // Check for specific lock conditions with explanations
    if (currentStatus === TaskStatus.CLIENT_APPROVAL && !hasDelegatedTask) {
      return {
        isDisabled: true,
        message:
          'This task is waiting for approval from the delegating organization. No changes can be made until they respond.',
      };
    }

    if (
      isMiddleCompany &&
      currentStatus === TaskStatus.ACTIVE &&
      getAvailableStatuses.length === 1
    ) {
      return {
        isDisabled: true,
        message:
          tTaskDetail.delegatedCannotChangeStatus,
      };
    }

    if (
      isDelegatedTask &&
      !isMiddleCompany &&
      currentStatus === TaskStatus.ACTIVE &&
      getAvailableStatuses.length === 1
    ) {
      return {
        isDisabled: true,
        message:
          'Limited status options available for delegated tasks. You can work on the task or submit it for client approval when complete.',
      };
    }

    if (getAvailableStatuses.length <= 1) {
      return {
        isDisabled: true,
        message:
          "Status cannot be changed at this time based on current workflow state and your organization's role.",
      };
    }

    return {
      isDisabled: false,
      message: null,
    };
  }, [
    getAvailableStatuses,
    values.status,
    task?.status,
    task?.delegateTaskId,
    task?.originalTaskId,
    tTaskDetail.delegatedCannotChangeStatus,
  ]);

  // Task type options come from the TaskCategory API (active categories only).
  // If the task being edited has a taskType that no longer exists in the active
  // list (e.g. deactivated or deleted category), we still include it so the
  // dropdown doesn't render empty.
  const { data: activeTaskCategories = [] } = useActiveTaskCategories();
  const taskTypeOptions: SelectOption[] = useMemo(() => {
    const fromApi: SelectOption[] = activeTaskCategories.map((category) => ({
      value: category.name,
      label: category.name,
    }));
    const currentValue = values.taskType as string | undefined;
    if (
      currentValue &&
      !fromApi.some((option) => option.value === currentValue)
    ) {
      return [{ value: currentValue, label: currentValue }, ...fromApi];
    }
    return fromApi;
  }, [activeTaskCategories, values.taskType]);

  // Event handlers
  const handleStatusChange = async (newStatus: string) => {
    // Validate that the new status is allowed
    if (!getAvailableStatuses.includes(newStatus as TaskStatus)) {
      console.warn('Status change not allowed:', {
        currentStatus: values.status,
        newStatus,
        userRole,
      });
      return;
    }

    // For new tasks, just update form value
    if (!task?.taskId || newStatus === values.status) {
      setFieldValue('status', newStatus);
      return;
    }

    // Check if user is trying to mark task as complete with incomplete subtasks
    const isHighPrivilegeUser = [
      UserRoles.PROJECT_MANAGER,
      UserRoles.ADMIN,
      UserRoles.OWNER,
    ].includes(userRole as UserRoles);

    const statusLabel = getTranslatedTaskStatusLabel(newStatus, tTaskDetail);

    // Pre-COMPLETE invoice gate: if the task still has unbilled materials/equipment,
    // surface a heads-up modal first. Informational only — "Complete anyway" continues
    // through the same downstream checks (subtasks, generic confirm). All roles that
    // can complete a task see this, including SUPERINTENDENT (they can't invoice but
    // need to know to escalate). The check is async and failure-tolerant: if the
    // backend errors, we fall through to the existing flow rather than block the user.
    if (newStatus === TaskStatus.COMPLETE) {
      try {
        const summary = await invoiceService.getTaskInvoicePendingSummary(task.taskId);
        if (summary.materialCount + summary.equipmentCount > 0) {
          setPendingInvoiceCheck({ newStatus, statusLabel, ...summary });
          setShowInvoicePendingDialog(true);
          return;
        }
      } catch (err) {
        console.warn('Invoice pending summary check failed; continuing without warning', err);
      }
    }

    if (
      newStatus === TaskStatus.COMPLETE &&
      isHighPrivilegeUser &&
      incompleteSubtasksCount > 0
    ) {
      // Show special confirmation dialog for incomplete subtasks
      setPendingCompleteStatusChange({
        newStatus,
        statusLabel,
        incompleteSubtasksCount,
      });
      setShowIncompleteSubtasksDialog(true);
      return;
    }

    // For existing tasks, show confirmation dialog for critical status changes
    const requiresConfirmation = [
      TaskStatus.COMPLETE,
      TaskStatus.REJECTED,
      TaskStatus.CLIENT_APPROVAL,
      TaskStatus.CANCELED,
      TaskStatus.DELEGATION_CANCELED,
    ].includes(newStatus as TaskStatus);

    if (requiresConfirmation) {
      setPendingStatusChange({ newStatus, statusLabel });
      setShowConfirmDialog(true);
    } else {
      await executeStatusChange(newStatus);
    }
  };

  const executeStatusChange = async (newStatus: string) => {
    setStatusUpdating(true);
    let success = false;

    try {
      // Always use updateTaskStatus for consistent delegation propagation and status history
      success = await updateTaskStatus({
        taskId: task!.taskId!,
        newStatus,
        previousStatus: values.status,
        reason:
          newStatus === TaskStatus.REJECTED && rejectionReason
            ? rejectionReason
            : undefined,
      });

      if (success) {
        setFieldValue('status', newStatus);
        // Trigger refresh of task data in parent component
        onStatusUpdate?.();
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      success = false;
    }

    setStatusUpdating(false);
  };

  const handleConfirmStatusChange = async () => {
    if (pendingStatusChange) {
      await executeStatusChange(pendingStatusChange.newStatus);
    }
    setShowConfirmDialog(false);
    setPendingStatusChange(null);
    setRejectionReason('');
  };

  const handleCancelStatusChange = () => {
    setShowConfirmDialog(false);
    setPendingStatusChange(null);
    setRejectionReason('');
  };

  const handleConfirmCompleteWithIncompleteSubtasks = async () => {
    if (pendingCompleteStatusChange) {
      await executeStatusChange(pendingCompleteStatusChange.newStatus);
    }
    setShowIncompleteSubtasksDialog(false);
    setPendingCompleteStatusChange(null);
  };

  // "Complete anyway" on the invoice-pending warning — replay the rest of the gating
  // flow (subtasks dialog, generic confirm) but skip the invoice check we just acked.
  const handleConfirmInvoicePending = async () => {
    const pending = pendingInvoiceCheck;
    setShowInvoicePendingDialog(false);
    setPendingInvoiceCheck(null);
    if (!pending) return;

    const isHighPrivilegeUser = [
      UserRoles.PROJECT_MANAGER,
      UserRoles.ADMIN,
      UserRoles.OWNER,
    ].includes(userRole as UserRoles);

    if (isHighPrivilegeUser && incompleteSubtasksCount > 0) {
      setPendingCompleteStatusChange({
        newStatus: pending.newStatus,
        statusLabel: pending.statusLabel,
        incompleteSubtasksCount,
      });
      setShowIncompleteSubtasksDialog(true);
      return;
    }

    // Invoice warning is itself the COMPLETE confirmation — no need to also show
    // the generic "are you sure" dialog on top of it.
    await executeStatusChange(pending.newStatus);
  };

  const handleCancelInvoicePending = () => {
    setShowInvoicePendingDialog(false);
    setPendingInvoiceCheck(null);
  };

  const handleCancelCompleteWithIncompleteSubtasks = () => {
    setShowIncompleteSubtasksDialog(false);
    setPendingCompleteStatusChange(null);
  };

  // Subtask management functions - now work with parent state
  const handleAddSubtasks = (taskIds: string[]) => {
    // Add to pending additions and remove from pending removals if they were there
    const newAdditions = taskIds.filter(
      (id) => !propSubtasksToAdd.includes(id),
    );
    const updatedToAdd = [...propSubtasksToAdd, ...newAdditions];
    const updatedToRemove = propSubtasksToRemove.filter(
      (id) => !taskIds.includes(id),
    );

    // Call parent with both updated values at once to avoid stale prop issue
    if (onSubtasksChange) {
      onSubtasksChange(updatedToAdd, updatedToRemove);
    }
    setShowAddSubtaskModal(false);
  };

  const handleRemoveSubtask = (taskId: string) => {
    const isPendingRemoval = propSubtasksToRemove.includes(taskId);
    const isPendingAddition = propSubtasksToAdd.includes(taskId);

    let updatedToAdd = propSubtasksToAdd;
    let updatedToRemove = propSubtasksToRemove;

    if (isPendingRemoval) {
      // Undo the pending removal
      updatedToRemove = propSubtasksToRemove.filter((id) => id !== taskId);
    } else if (isPendingAddition) {
      // Undo the pending addition
      updatedToAdd = propSubtasksToAdd.filter((id) => id !== taskId);
    } else {
      // If it's currently a real subtask, add to pending removals
      if (
        tasks.some(
          (t) => t.parentTaskId === task?.taskId && t.taskId === taskId,
        )
      ) {
        updatedToRemove = [...propSubtasksToRemove, taskId];
      }
    }

    // Call parent with both updated values at once
    if (onSubtasksChange) {
      onSubtasksChange(updatedToAdd, updatedToRemove);
    }
  };

  // Get calculated visual status for existing tasks
  const displayInfo = useMemo(() => {
    if (!task?.taskId) return null;
    return getTaskStatusDisplay(task);
  }, [task]);

  const isUpcoming = displayInfo?.displayValue === TaskDisplayValues.UPCOMING;

  // Soft guidance only: the range a new task is prefilled with. Tasks may
  // still be dated outside it (projects get extended), so no min/max.
  const dateRangeHint = useMemo(() => {
    const scope = parentTask
      ? { label: 'Parent task dates', start: parentTask.startTime, end: parentTask.endTime }
      : { label: 'Project dates', start: project?.startDate, end: project?.endDate };
    const start = formatDateOnly(scope.start);
    const end = formatDateOnly(scope.end);
    if (!start && !end) return null;
    return `${scope.label}: ${start || '…'} – ${end || '…'}`;
  }, [parentTask, project?.startDate, project?.endDate]);

  return (
    <>
      <DelegationInfoBanner task={task} />

      {/* Visual Status Badge - Show calculated status for existing tasks */}
      {task?.taskId && displayInfo && isUpcoming && (
        <div className="flex items-center mb-4">
          <Badge
            variant="primary"
            size="md"
            className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100">
            {/* Sprint 28 change request (global sweep, defect fix). `displayInfo.label` used
                to be raw English text ('Upcoming' — this badge only renders when
                `isUpcoming`) — see `@utils/taskDisplay.ts`'s `TaskDisplayInfo.label` comment. */}
            {translateTaskDisplayValue(displayInfo.displayValue, tTaskDetail)}
          </Badge>
        </div>
      )}

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormGroup
          label="Name *"
          htmlFor="name"
          error={errors.name && touched.name ? errors.name : undefined}>
          <Input
            name="name"
            placeholder="Enter task name"
            value={values.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue('name', e.target.value)
            }
            onBlur={handleBlur}
            className={errors.name && touched.name ? 'border-red-500' : ''}
            data-testid="task-form-name-input"
          />
        </FormGroup>

        <StatusField
          statusOptions={statusOptions}
          values={values}
          task={task}
          errors={errors}
          touched={touched}
          statusUpdating={statusUpdating}
          statusLoading={statusLoading}
          statusLockInfo={statusLockInfo}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormGroup
          label="Start Time *"
          htmlFor="startTime"
          error={
            errors.startTime && touched.startTime ? errors.startTime : undefined
          }>
          <Input
            type="date"
            name="startTime"
            value={values.startTime}
            onChange={(e) => setFieldValue('startTime', e.target.value)}
            onBlur={handleBlur}
            className={`block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 ${
              errors.startTime && touched.startTime ? 'border-red-500' : ''
            }`}
          />
        </FormGroup>

        <FormGroup
          label="End Time *"
          htmlFor="endTime"
          error={
            errors.endTime && touched.endTime ? errors.endTime : undefined
          }>
          <Input
            type="date"
            name="endTime"
            value={values.endTime}
            onChange={(e) => setFieldValue('endTime', e.target.value)}
            onBlur={handleBlur}
            className={`block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 ${
              errors.endTime && touched.endTime ? 'border-red-500' : ''
            }`}
          />
        </FormGroup>
      </div>
      {dateRangeHint && (
        <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
          {dateRangeHint}
        </p>
      )}

      {/* Task Type */}
      <div
        className={`grid gap-4 grid-cols-1 ${values.taskType === 'Other' ? ' md:grid-cols-2' : ' md:grid-cols-1 '}`}>
        <FormGroup
          label="Task Type *"
          htmlFor="taskType"
          error={
            errors.taskType && touched.taskType ? errors.taskType : undefined
          }>
          <ReactSelect
            key={`taskType-${task?.taskId || 'new'}-${values.taskType}`}
            options={taskTypeOptions}
            value={taskTypeOptions.find(
              (option) => option.value === values.taskType,
            )}
            onChange={async (option: any) => {
              const newValue = option?.value || '';
              setFieldValue('taskType', newValue);
              // Mark as touched first
              setFieldTouched('taskType', true);
              // Then force validation immediately
              await validateField('taskType');
              if (newValue !== 'Other') {
                setFieldValue('otherType', null);
              }
            }}
            onBlur={() => setFieldTouched('taskType', true)}
            placeholder="Select task type"
            error={!!(errors.taskType && touched.taskType)}
            helperText={errors.taskType && touched.taskType ? errors.taskType : undefined}
          />
        </FormGroup>

        {/* Other Type */}
        {values.taskType === 'Other' && (
          <FormGroup
            label="Add Other Type *"
            htmlFor="otherType"
            error={
              errors.otherType && touched.otherType
                ? errors.otherType
                : undefined
            }>
            <Input
              name="otherType"
              placeholder="Specify other type"
              value={values.otherType || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('otherType', e.target.value)
              }
              onBlur={handleBlur}
              className={
                errors.otherType && touched.otherType ? 'border-red-500' : ''
              }
            />
          </FormGroup>
        )}
      </div>

      <FormGroup
        label="Description"
        htmlFor="description"
        error={
          errors.description && touched.description
            ? errors.description
            : undefined
        }>
        <RichTextEditor
          name="description"
          id="description"
          placeholder="Enter task description"
          value={values.description}
          onChange={(html) => setFieldValue('description', html)}
          onBlur={() => setFieldTouched('description', true)}
        />
      </FormGroup>

      {/* Subtasks Section */}
      <SubtasksSection
        task={task}
        existingSubtasks={existingSubtasks}
        subtasksToAdd={propSubtasksToAdd}
        subtasksToRemove={propSubtasksToRemove}
        availableTasksForSubtasks={availableTasksForSubtasks}
        allTasks={tasks}
        hasActiveBudgetRequest={hasActiveBudgetRequest}
        onAddSubtaskClick={() => setShowAddSubtaskModal(true)}
        onRemoveSubtask={handleRemoveSubtask}
        onCreateSubtaskClick={onCreateSubtaskClick}
        onImportTemplateClick={onImportTemplateClick}
      />

      {/* Status Change Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog && !!pendingStatusChange}
        title={tTaskDetail.confirmStatusChangeTitle}
        message="This change will be applied immediately and may be irreversible."
        statusLabel={pendingStatusChange?.statusLabel || ''}
        newStatus={pendingStatusChange?.newStatus || ''}
        rejectionReason={rejectionReason}
        onReasonChange={setRejectionReason}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
        isLoading={statusUpdating}
      />

      {/* Add Subtask Modal - Show for both existing and new tasks */}
      <AddSubtaskModal
        isOpen={showAddSubtaskModal}
        availableTasks={availableTasksForSubtasks}
        onAdd={handleAddSubtasks}
        onCancel={() => setShowAddSubtaskModal(false)}
      />

      {/* Incomplete Subtasks Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showIncompleteSubtasksDialog && !!pendingCompleteStatusChange}
        title={tTaskDetail.completeWithIncompleteSubtasksTitle}
        message={tTaskDetail.completeWithIncompleteSubtasksMessage.replace(
          '{subtasks}',
          (() => {
            const n = pendingCompleteStatusChange?.incompleteSubtasksCount || 0;
            // Sprint 31, story-158 (house style §13.2): `count === 1`, not `count > 1` — the
            // seven-site zero-handling bug, fixed here too while converting.
            return (n === 1 ? tTaskDetail.incompleteSubtaskOne : tTaskDetail.incompleteSubtaskOther).replace(
              '{n}',
              String(n),
            );
          })(),
        )}
        statusLabel={pendingCompleteStatusChange?.statusLabel || ''}
        newStatus={pendingCompleteStatusChange?.newStatus || ''}
        rejectionReason=""
        onReasonChange={() => {}}
        onConfirm={handleConfirmCompleteWithIncompleteSubtasks}
        onCancel={handleCancelCompleteWithIncompleteSubtasks}
        isLoading={statusUpdating}
      />

      {/* Pre-COMPLETE Invoice Pending Items Dialog (informational) */}
      <ConfirmationDialog
        isOpen={showInvoicePendingDialog && !!pendingInvoiceCheck}
        title={tTaskDetail.pendingInvoiceItemsTitle}
        message={
          pendingInvoiceCheck
            ? tTaskDetail.taskInvoicePendingMessage
                .replace(
                  '{material}',
                  (pendingInvoiceCheck.materialCount === 1
                    ? tTaskDetail.materialUnbilledOne
                    : tTaskDetail.materialUnbilledOther
                  ).replace('{n}', String(pendingInvoiceCheck.materialCount)),
                )
                .replace(
                  '{equipment}',
                  (pendingInvoiceCheck.equipmentCount === 1
                    ? tTaskDetail.equipmentUnbilledOne
                    : tTaskDetail.equipmentUnbilledOther
                  ).replace('{n}', String(pendingInvoiceCheck.equipmentCount)),
                ) +
              (pendingInvoiceCheck.sampleNames.length > 0
                ? `${tTaskDetail.pendingSamplesPrefix}${pendingInvoiceCheck.sampleNames.join(', ')}${pendingInvoiceCheck.materialCount + pendingInvoiceCheck.equipmentCount > pendingInvoiceCheck.sampleNames.length ? '…' : ''}`
                : '')
            : ''
        }
        statusLabel={pendingInvoiceCheck?.statusLabel || ''}
        newStatus={pendingInvoiceCheck?.newStatus || ''}
        rejectionReason=""
        onReasonChange={() => {}}
        onConfirm={handleConfirmInvoicePending}
        onCancel={handleCancelInvoicePending}
        isLoading={statusUpdating}
        confirmLabel={tTaskDetail.completeAnywayButton}
      />
    </>
  );
};
